import type { PlainMessage } from '@bufbuild/protobuf';

import type { NoteOption } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import {
  NOTE_CODE_SYSTEMS,
  NOTE_OPTION_CODE_FORMAT,
  type NoteEditorBlockDef,
} from '../config';

// A choice's own options, which win over its Values when it carries them.
export const listNoteBlockOptions = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
): PlainMessage<NoteOption>[] =>
  block.Kind.case === 'Choice' || block.Kind.case === 'MultiChoice'
    ? block.Kind.value.Options ?? []
    : [];

// What an answer, a show_when and a default hold for an option, as scribe
// stores it: its code when the block's options carry codes, else its label.
export const noteOptionValue = (option: Pick<NoteOption, 'Code' | 'Label'>) =>
  option.Code || option.Label;

// The option a value of the block stores, as scribe finds it: by its code on
// a block whose options carry codes, by its label otherwise.
const findNoteChoiceOption = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
  value: string,
) => {
  const options = listNoteBlockOptions(block);
  const coded = options.some((option) => option.Code);
  return options.find((option) =>
    coded ? option.Code === value : option.Label === value,
  );
};

// A diagnosis field (one with a code system) shows and prints an option
// after its code: "K07.20 Дистальный прикус".
const codedText = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
  option: Pick<NoteOption, 'Code'>,
  text: string,
) => {
  const { CodeSystem } = block.Kind.value as { CodeSystem?: number };
  if (!CodeSystem || !option.Code) {
    return text;
  }
  return text ? `${option.Code} ${text}` : option.Code;
};

// What a value reads as: its option's label, after its code on a diagnosis
// field; an own value, a value of a block without options and a list item
// read as they are.
export const noteChoiceLabel = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
  value: string,
) => {
  const option = findNoteChoiceOption(block, value);
  return option ? codedText(block, option, option.Label) : value;
};

// How a value prints in the note, as scribe prints it: its option's Print,
// else its label, after its code on a diagnosis field.
export const noteChoicePrint = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
  value: string,
) => {
  const option = findNoteChoiceOption(block, value);
  return option
    ? codedText(block, option, option.Print || option.Label)
    : value;
};

// The picture key of a value's option ("profile.convex"), when it has one.
export const noteChoiceImage = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
  value: string,
) => findNoteChoiceOption(block, value)?.Image;

// The spellings a typed text may name a value by: its label and its print,
// with and without a diagnosis code.
export const listNoteChoiceSpellings = (
  block: Pick<NoteEditorBlockDef, 'Kind'>,
  value: string,
) => {
  const option = findNoteChoiceOption(block, value);
  return [
    noteChoiceLabel(block, value),
    noteChoicePrint(block, value),
    ...(option ? [option.Label, option.Print ?? ''] : []),
  ].filter(Boolean);
};

// Whether scribe takes a code for an option: in its code system's format on
// a diagnosis field, a neutral code otherwise.
export const isNoteOptionCode = (code: string, codeSystem?: number) =>
  (codeSystem
    ? NOTE_CODE_SYSTEMS[codeSystem]?.format
    : NOTE_OPTION_CODE_FORMAT
  )?.test(code) ?? false;
